import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClaimsSummary, isLive, useCounterclaimCandidates } from "../../hooks/useClaimsSummary";
import {
  useCaseActionBoard,
  useSetServiceAnchor,
  useSetServiceAnchorBulk,
  useAdvanceSegment,
  useAdvanceSegmentsBulk,
  useUndoLastAction,
  useClearActionHistory,
  useActionPlaybook,
  useRecordMtdRuling,
  type ActionBoard as Board,
  type PlaybookEntry,
  type CaseSegment,
  type SegmentAction,
  type BoardFiling,
  type PartyRow,
} from "../../hooks/useCaseActionBoard";

const PlaybookContext = createContext<Map<string, PlaybookEntry>>(new Map());

const MODE_KEY = "ldb-docket-mode";
const FOCUS_KEY = "ldb-docket-focus";
type Mode = "solo" | "party";

/** Litigation action board — case history + the live docket engine, bound to
 *  the individual defendant. Solo view focuses one defendant; party view shows
 *  all and lets a move fan out to each individually. */
function ActionBoard({
  caseId,
  focusPartyId,
}: {
  caseId: string;
  /** When provided (board surface), controls solo focus from outside. */
  focusPartyId?: string | null;
}) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useCaseActionBoard(caseId);
  const { data: playbook } = useActionPlaybook(data?.ruleset?.code ?? "nc-business-court");
  const playbookMap = new Map((playbook ?? []).map((e) => [e.action_key, e]));
  const [mode, setMode] = useState<Mode>(
    () => (localStorage.getItem(MODE_KEY) as Mode) || "solo",
  );
  const [focus, setFocus] = useState<string | null>(
    () => localStorage.getItem(FOCUS_KEY),
  );

  function changeMode(m: Mode) {
    setMode(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* ignore */ }
  }
  function changeFocus(id: string) {
    setFocus(id);
    try { localStorage.setItem(FOCUS_KEY, id); } catch { /* ignore */ }
  }

  const defendants = data?.parties ?? [];
  // resolve the focused defendant (fall back to the first)
  const focusId =
    (focus && defendants.some((d) => d.party_id === focus) && focus) ||
    defendants[0]?.party_id ||
    null;
  const controlled = focusPartyId != null;
  const onBoardPage = focusPartyId !== undefined;
  const effMode: Mode = controlled ? "solo" : mode;
  const effFocus = controlled ? focusPartyId : focusId;

  return (
    <PlaybookContext.Provider value={playbookMap}>
    <div className="flex flex-col rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
        <span className="text-bone-dim">case docket</span>
        <span className="flex items-center gap-3 text-bone-mute/70">
          {!onBoardPage && (
            <button
              type="button"
              onClick={() => navigate(`/case/${caseId}/board`)}
              className="text-blue-bright/80 transition-colors hover:text-blue-bright"
            >
              open the board ▸
            </button>
          )}
          {data?.ruleset ? data.ruleset.version : "// rules"}
        </span>
      </div>

      <div className="flex flex-col gap-4 px-5 py-5">
        {isLoading && (
          <p className="font-display text-sm text-bone-mute">reading the docket…</p>
        )}
        {error && (
          <p className="font-display text-sm text-ember-hot">{error.message}</p>
        )}

        {data && defendants.length > 0 && (
          <ScopeControls
            mode={effMode}
            onMode={changeMode}
            defendants={defendants}
            focusId={effFocus}
            onFocus={changeFocus}
          />
        )}

        {data?.needs_anchor && data.anchor_filing && (
          <PartyAnchors
            caseId={caseId}
            filing={data.anchor_filing}
            defendants={defendants}
          />
        )}

        {data && (
          <ScopeBoard caseId={caseId} board={data} mode={effMode} focusId={effFocus} />
        )}

        {data && data.filings.length > 0 && (
          <CaseHistory filings={data.filings} />
        )}

        {data && <Trail segments={data.segments} parties={defendants} />}

        {data && data.segments.length > 0 && (
          <DocketControls
            caseId={caseId}
            hasActions={data.segments.some((s) => !!s.opened_by_action_key)}
          />
        )}

        {data?.ruleset && <RulesetFooter ruleset={data.ruleset} />}
      </div>
    </div>
    </PlaybookContext.Provider>
  );
}

/* ----------------------------------------------------- scope controls -- */

function firstName(name: string): string {
  // "ADAM ISLEY" -> "Adam Isley" trimmed to a compact chip label
  const t = name.replace(/,?\s*(INC\.?|LLC|L\.?L\.?C\.?)$/i, "").trim();
  return t
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function ScopeControls({
  mode,
  onMode,
  defendants,
  focusId,
  onFocus,
}: {
  mode: Mode;
  onMode: (m: Mode) => void;
  defendants: PartyRow[];
  focusId: string | null;
  onFocus: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70">
          view
        </span>
        <div className="flex overflow-hidden rounded border border-steel-dark">
          {(["solo", "party"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onMode(m)}
              className={`px-3 py-1 font-display text-sm font-light uppercase tracking-[0.1em] transition-colors ${
                mode === m
                  ? "bg-ember/30 text-bone"
                  : "bg-iron/60 text-bone-mute/70 hover:text-bone-dim"
              }`}
            >
              {m === "solo" ? "solo" : "all defendants"}
            </button>
          ))}
        </div>
      </div>

      {mode === "solo" && defendants.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {defendants.map((d) => {
            const active = d.party_id === focusId;
            return (
              <button
                key={d.party_id}
                type="button"
                onClick={() => onFocus(d.party_id)}
                className={`rounded border px-2.5 py-1 font-display text-sm font-light tracking-tight transition-colors ${
                  active
                    ? "border-blue-bright/60 bg-iron-2/80 text-blue-bright"
                    : "border-steel-dark bg-iron/50 text-bone-mute hover:text-bone-dim"
                }`}
              >
                {firstName(d.display_name)}
                {!d.anchored ? " ·" : ""}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------- anchoring -- */

function PartyAnchors({
  caseId,
  filing,
  defendants,
}: {
  caseId: string;
  filing: { id: string; title: string };
  defendants: PartyRow[];
}) {
  const unanchored = defendants.filter((d) => !d.anchored);
  const setOne = useSetServiceAnchor(caseId);
  const setAll = useSetServiceAnchorBulk(caseId);
  const [dates, setDates] = useState<Record<string, string>>({});
  const [bulkDate, setBulkDate] = useState("");

  if (unanchored.length === 0) return null;

  return (
    <div className="rounded border border-ember-bright/50 bg-ember/20 p-4">
      <p className="font-display text-base font-light uppercase tracking-[0.12em] text-ember-hot">
        anchor the clock · per defendant
      </p>
      <p className="mt-2 font-display text-sm leading-relaxed text-bone-dim">
        Each defendant's response deadline runs from <span className="text-bone">their own date of service</span>{" "}
        of the {filing.title}. Enter each — or set one date for all if they were served together.
      </p>

      {unanchored.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-b border-steel-dark/60 pb-3">
          <span className="font-display text-sm text-bone-mute">served together:</span>
          <input
            type="date"
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
            className="rounded border border-steel-dark bg-iron px-3 py-1.5 font-display text-sm text-bone focus:border-ember-bright/70 focus:outline-none"
          />
          <button
            type="button"
            disabled={!bulkDate || setAll.isPending}
            onClick={() =>
              setAll.mutate({
                filingId: filing.id,
                partyIds: unanchored.map((d) => d.party_id),
                servedAt: bulkDate,
              })
            }
            className="border border-ember-bright/60 bg-ember/30 px-4 py-1.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone transition-colors hover:bg-ember/50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {setAll.isPending ? "anchoring…" : "set all"}
          </button>
        </div>
      )}

      <div className="mt-3 flex flex-col gap-2">
        {unanchored.map((d) => (
          <div key={d.party_id} className="flex flex-wrap items-center gap-2">
            <span className="min-w-[9rem] font-display text-sm text-bone-dim">
              {firstName(d.display_name)}
            </span>
            <input
              type="date"
              value={dates[d.party_id] ?? ""}
              onChange={(e) =>
                setDates((p) => ({ ...p, [d.party_id]: e.target.value }))
              }
              className="rounded border border-steel-dark bg-iron px-3 py-1.5 font-display text-sm text-bone focus:border-ember-bright/70 focus:outline-none"
            />
            <button
              type="button"
              disabled={!dates[d.party_id] || setOne.isPending}
              onClick={() =>
                setOne.mutate({
                  filingId: filing.id,
                  partyId: d.party_id,
                  servedAt: dates[d.party_id]!,
                })
              }
              className="border border-steel-dark bg-iron-2/60 px-3 py-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-dim transition-colors hover:border-ember-bright/50 hover:text-bone disabled:cursor-not-allowed disabled:opacity-40"
            >
              anchor
            </button>
          </div>
        ))}
      </div>
      {(setOne.error || setAll.error) && (
        <p className="mt-2 font-display text-sm text-ember-hot">
          {(setOne.error || setAll.error)?.message}
        </p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- scope board -- */

function ScopeBoard({
  caseId,
  board,
  mode,
  focusId,
}: {
  caseId: string;
  board: Board;
  mode: Mode;
  focusId: string | null;
}) {
  const segs = board.segments;
  const caseLevel = segs.filter((s) => s.party_id === null);

  if (mode === "solo") {
    const d = board.parties.find((p) => p.party_id === focusId) ?? null;
    const mine = segs.filter((s) => s.party_id === focusId);
    return (
      <div className="flex flex-col gap-4">
        {d && <PartyHeadline party={d} />}
        <ActionableSet caseId={caseId} segments={mine} />
        <PassiveSet segments={mine} />
        {caseLevel.length > 0 && (
          <div className="flex flex-col gap-3">
            <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70">
              case-wide (all parties)
            </p>
            <ActionableSet caseId={caseId} segments={caseLevel} />
            <PassiveSet segments={caseLevel} />
          </div>
        )}
      </div>
    );
  }

  // party mode — every defendant, plus a group fan-out and the case-wide block
  return (
    <div className="flex flex-col gap-5">
      <GroupMove caseId={caseId} board={board} />
      {board.parties.map((d) => {
        const mine = segs.filter((s) => s.party_id === d.party_id);
        return (
          <div key={d.party_id} className="flex flex-col gap-3 border-l-2 border-steel-dark/60 pl-3">
            <PartyHeadline party={d} compact />
            {mine.length === 0 ? (
              <p className="font-display text-sm text-bone-mute/60">
                no service date set — anchor above.
              </p>
            ) : (
              <>
                <ActionableSet caseId={caseId} segments={mine} />
                <PassiveSet segments={mine} />
              </>
            )}
          </div>
        );
      })}
      {caseLevel.length > 0 && (
        <div className="flex flex-col gap-3">
          <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70">
            case-wide (all parties)
          </p>
          <ActionableSet caseId={caseId} segments={caseLevel} />
          <PassiveSet segments={caseLevel} />
        </div>
      )}
    </div>
  );
}

function PartyHeadline({ party, compact }: { party: PartyRow; compact?: boolean }) {
  const urgent = party.overdue || (party.days_remaining ?? 99) <= 7;
  return (
    <div
      className={`rounded border p-3 ${
        party.deadline_date == null
          ? "border-steel-dark/70 bg-iron/40"
          : urgent
            ? "border-ember-bright/50 bg-ember/15"
            : "border-blue-bright/40 bg-iron-2/60"
      }`}
    >
      <p className="font-display text-base font-light tracking-tight text-bone">
        {firstName(party.display_name)}
        {party.party_type === "entity" ? " (entity)" : ""}
      </p>
      {party.deadline_date ? (
        <p
          className={`font-display ${compact ? "text-base" : "text-lg"} font-light ${
            party.overdue ? "text-ember-hot" : "text-bone-dim"
          }`}
        >
          {daysLabel(party.days_remaining, party.overdue)}{" "}
          <span className="text-sm text-bone-mute">· responsive pleading due {fmtDate(party.deadline_date)}</span>
        </p>
      ) : (
        <p className="font-display text-sm text-bone-mute/70">
          {party.anchored ? "no open response deadline" : "awaiting date of service"}
        </p>
      )}
    </div>
  );
}

/* --------------------------------------------------------- group move -- */

function GroupMove({ caseId, board }: { caseId: string; board: Board }) {
  // defendants currently sitting at an open/acted response window
  const responseSegs = board.segments.filter(
    (s) =>
      s.party_id !== null &&
      s.stage_key === "pleading_response" &&
      (s.status === "open" || s.status === "acted") &&
      s.actions.length > 0,
  );
  const bulk = useAdvanceSegmentsBulk(caseId);
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (responseSegs.length < 2) return null;

  // actions available to ALL of them (intersection by action_key)
  const counts: Record<string, { action: SegmentAction; n: number }> = {};
  for (const s of responseSegs) {
    for (const a of s.actions) {
      counts[a.action_key] = { action: a, n: (counts[a.action_key]?.n ?? 0) + 1 };
    }
  }
  const shared = Object.values(counts)
    .filter((c) => c.n === responseSegs.length)
    .map((c) => c.action);
  const ids = responseSegs.map((s) => s.id);

  if (shared.length === 0) return null;

  return (
    <div className="rounded border border-blue-bright/40 bg-iron-2/50 p-3">
      <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-blue-bright">
        group move · applies to {responseSegs.length} defendants individually
      </p>
      <p className="mt-1 font-display text-sm text-bone-mute/70">
        files the same move for each defendant on their own clock.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {shared.map((a) => (
          <button
            key={a.action_key}
            type="button"
            disabled={bulk.isPending}
            onClick={() => setOpenKey(openKey === a.action_key ? null : a.action_key)}
            className="rounded border border-steel-dark bg-iron/60 px-2.5 py-1 font-display text-sm font-light tracking-tight text-bone-dim transition-colors hover:border-blue-bright/60 hover:text-bone disabled:opacity-40"
          >
            {a.label} → all
          </button>
        ))}
      </div>
      {openKey && (
        <div className="mt-2 flex items-center gap-2">
          <span className="font-display text-sm text-bone-mute">
            file “{shared.find((a) => a.action_key === openKey)?.label}” for all {responseSegs.length}?
          </span>
          <button
            type="button"
            disabled={bulk.isPending}
            onClick={() =>
              bulk.mutate(
                { segmentIds: ids, actionKey: openKey },
                { onSuccess: () => setOpenKey(null) },
              )
            }
            className="text-blue-bright hover:underline"
          >
            {bulk.isPending ? "filing…" : "confirm"}
          </button>
          <button
            type="button"
            onClick={() => setOpenKey(null)}
            className="text-bone-mute/60 hover:text-bone-dim"
          >
            cancel
          </button>
        </div>
      )}
      {bulk.error && (
        <p className="mt-2 font-display text-sm text-ember-hot">{bulk.error.message}</p>
      )}
    </div>
  );
}

/* ---------------------------------------------------- actionable set -- */

function daysLabel(n: number | null, overdue: boolean): string {
  if (n === null) return "no deadline set";
  if (overdue) return `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} overdue`;
  if (n === 0) return "due today";
  return `${n} day${n === 1 ? "" : "s"}`;
}

function ownerLabel(owner: string): string {
  switch (owner) {
    case "responding_party": return "You / this defendant";
    case "plaintiff": return "Opposing party";
    case "court": return "The Court";
    case "any_party": return "Any party";
    case "all_parties": return "All parties";
    default: return owner;
  }
}

function byDeadline(a: CaseSegment, b: CaseSegment): number {
  const ad = a.deadline_date ?? "9999";
  const bd = b.deadline_date ?? "9999";
  return ad < bd ? -1 : ad > bd ? 1 : 0;
}

/** Render the open/acted segments (with actions) in a subset, grouped your
 *  moves vs record-an-outcome. */
function ActionableSet({ caseId, segments }: { caseId: string; segments: CaseSegment[] }) {
  const actionable = segments.filter(
    (s) => (s.status === "open" || s.status === "acted") && s.actions.length > 0,
  );
  const yours = actionable
    .filter((s) => ["responding_party", "any_party", "all_parties"].includes(s.owner))
    .sort(byDeadline);
  const outcomes = actionable
    .filter((s) => ["court", "plaintiff"].includes(s.owner))
    .sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  if (yours.length === 0 && outcomes.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {yours.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
            your moves — stack several in parallel
          </p>
          {yours.map((s) => <SegmentCard key={s.id} caseId={caseId} seg={s} />)}
        </div>
      )}
      {outcomes.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
            record an outcome
          </p>
          {outcomes.map((s) => <SegmentCard key={s.id} caseId={caseId} seg={s} />)}
        </div>
      )}
    </div>
  );
}

function PassiveSet({ segments }: { segments: CaseSegment[] }) {
  const passive = segments.filter(
    (s) => (s.status === "open" || s.status === "acted") && s.actions.length === 0,
  );
  if (passive.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {passive.map((s) => (
        <div key={s.id} className="rounded border border-steel-dark/70 bg-iron/40 px-4 py-3">
          <p className="font-display text-base font-light text-bone-dim">{s.title}</p>
          <p className="font-display text-sm text-bone-mute/70">
            {ownerLabel(s.owner)}
            {s.deadline_date ? ` · ${daysLabel(s.days_remaining, s.overdue)} (${fmtDate(s.deadline_date)})` : ""}
            {s.rule_cite ? ` · ${s.rule_cite}` : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

function prettyAction(key: string): string {
  return key.replace(/_/g, " ");
}

function claimIdsOf(seg: CaseSegment): string[] {
  const ids = (seg.metadata as { claim_ids?: unknown } | null)?.claim_ids;
  return Array.isArray(ids) ? (ids as string[]) : [];
}

function SegmentCard({ caseId, seg }: { caseId: string; seg: CaseSegment }) {
  return (
    <div className="rounded border border-steel-dark/70 bg-iron/40 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="min-w-0 font-display text-base font-light text-bone-dim">{seg.title}</p>
        {seg.deadline_date && (
          <span className={`shrink-0 font-display text-sm ${seg.overdue ? "text-ember-hot" : "text-bone-mute"}`}>
            {daysLabel(seg.days_remaining, seg.overdue)}
          </span>
        )}
      </div>
      {seg.taken_actions.length > 0 && (
        <p className="mb-2 font-display text-sm text-bone-mute/70">
          in flight: {seg.taken_actions.map(prettyAction).join(", ")}
        </p>
      )}
      <div className="flex flex-col gap-2">
        {seg.stage_key === "mtd_pending" && claimIdsOf(seg).length > 0 ? (
          <MtdRulingForm caseId={caseId} seg={seg} />
        ) : (
          seg.actions.map((a) => (
            <ActionRow key={a.action_key} caseId={caseId} segmentId={seg.id} action={a} />
          ))
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------- MTD claim flows -- */

function MtdRulingForm({ caseId, seg }: { caseId: string; seg: CaseSegment }) {
  const ids = claimIdsOf(seg);
  const { data: claims } = useClaimsSummary(caseId);
  const record = useRecordMtdRuling(caseId);
  const byId = new Map((claims?.claims ?? []).map((c) => [c.id, c]));
  const [rulingDate, setRulingDate] = useState("");
  const [outcomes, setOutcomes] = useState<Record<string, string>>({});

  const opts = [
    { v: "survived", label: "survived" },
    { v: "dismissed_wo", label: "dismissed (w/o prejudice)" },
    { v: "dismissed_with", label: "dismissed (with prejudice)" },
  ];

  return (
    <div className="rounded border border-steel-dark bg-iron/60 p-3">
      <p className="mb-2 font-display text-base font-light text-bone-dim">
        Record the Court&#39;s ruling — per claim
      </p>
      <label className="mb-3 flex flex-col gap-1">
        <span className="font-display text-sm text-bone-mute">Date of the order</span>
        <input type="date" value={rulingDate} onChange={(e) => setRulingDate(e.target.value)}
          className="w-full rounded border border-steel-dark bg-iron px-3 py-1.5 font-display text-sm text-bone focus:border-blue-bright/70 focus:outline-none" />
      </label>
      <div className="flex flex-col gap-2">
        {ids.map((id) => {
          const c = byId.get(id);
          const val = outcomes[id] ?? "survived";
          return (
            <div key={id} className="rounded border border-steel-dark/70 bg-iron/40 px-3 py-2">
              <p className="mb-1 font-display text-sm text-bone-dim">{c?.claim_name ?? id}</p>
              <div className="flex flex-wrap gap-1.5">
                {opts.map((o) => (
                  <button key={o.v} type="button" onClick={() => setOutcomes((p) => ({ ...p, [id]: o.v }))}
                    className={`rounded border px-2 py-0.5 font-display text-sm transition-colors ${
                      val === o.v
                        ? o.v === "survived" ? "border-blue-bright/60 text-blue-bright" : "border-ember-bright/60 bg-ember/20 text-ember-hot"
                        : "border-steel-dark text-bone-mute hover:text-bone-dim"
                    }`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" disabled={!rulingDate || record.isPending}
          onClick={() => record.mutate({
            segmentId: seg.id, rulingDate,
            outcomes: ids.map((id) => ({ claim_id: id, outcome: outcomes[id] ?? "survived" })),
          })}
          className="border border-ember-bright/60 bg-ember/30 px-4 py-1.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone transition-colors hover:bg-ember/50 disabled:cursor-not-allowed disabled:opacity-40">
          {record.isPending ? "recording…" : "record ruling"}
        </button>
        <span className="font-display text-sm text-bone-mute/70">dismissed claims drop from the volley</span>
      </div>
      {record.error && <p className="mt-2 font-display text-sm text-ember-hot">{record.error.message}</p>}
    </div>
  );
}

function PlaybookPanel({ info }: { info: PlaybookEntry }) {
  return (
    <div className="mt-2 flex flex-col gap-2 rounded border border-steel-dark/60 bg-iron-2/40 p-3">
      <p className="font-display text-sm leading-snug text-bone-dim">{info.summary}</p>
      {info.when_used && (
        <div>
          <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">when it is used</p>
          <p className="font-display text-sm leading-snug text-bone-dim">{info.when_used}</p>
        </div>
      )}
      {info.consequences && (
        <div>
          <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">what it triggers</p>
          <p className="font-display text-sm leading-snug text-bone-dim">{info.consequences}</p>
        </div>
      )}
      {info.skeleton && (
        <div>
          <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">filing skeleton</p>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded bg-black/40 p-2 font-mono text-sm leading-snug text-bone-mute">{info.skeleton}</pre>
        </div>
      )}
      {info.cases.length > 0 && (
        <div>
          <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">case example</p>
          {info.cases.map((c, i) => (
            <p key={i} className="font-display text-sm leading-snug text-bone-dim">
              <span className="text-bone">{c.case_name}</span>
              {c.citation ? `, ${c.citation}` : ""}{c.decided_year ? ` (${c.decided_year})` : ""}
              {c.outcome ? ` — ${c.outcome}` : ""}
              {c.source_url ? (
                <>
                  {" "}
                  <a href={c.source_url} target="_blank" rel="noreferrer" className="text-blue-bright hover:underline">opinion ▸</a>
                </>
              ) : null}
            </p>
          ))}
        </div>
      )}
      <p className="font-display text-sm text-bone-mute/50">
        {(info.rule_cites ?? []).join(" · ")}
        {info.rule_cites && info.rule_cites.length ? " · " : ""}{info.edition}
      </p>
    </div>
  );
}

/* ------------------------------------------------ counterclaim editor -- */

interface CounterclaimDraft { name: string; target: string; summary: string }

/** When you file an Answer you may assert counterclaims against the plaintiff.
 *  Each one recorded here becomes a live claim on the board — a return volley,
 *  the mirror of dismissing one of theirs. (NCRCP 13(a): compulsory
 *  counterclaims arising from the same transaction must be raised now or waived.) */
function CounterclaimEditor({
  caseId,
  rows,
  onChange,
}: {
  caseId: string;
  rows: CounterclaimDraft[];
  onChange: (r: CounterclaimDraft[]) => void;
}) {
  const { data: candidates } = useCounterclaimCandidates(caseId);
  const set = (i: number, patch: Partial<CounterclaimDraft>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, { name: "", target: "", summary: "" }]);
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));
  const addFromCandidate = (name: string, summary: string) => {
    if (rows.some((r) => r.name.trim().toLowerCase() === name.trim().toLowerCase())) return;
    onChange([...rows, { name, target: "", summary }]);
  };
  const used = new Set(rows.map((r) => r.name.trim().toLowerCase()));
  const cc = candidates?.items ?? [];

  return (
    <div className="mb-3 flex flex-col gap-2 rounded border border-blue-bright/30 bg-iron-2/40 p-3">
      <div className="flex items-baseline justify-between">
        <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright">
          raise counterclaims (optional)
        </span>
        <span className="font-display text-sm text-bone-mute/60">fires a return volley at the plaintiff</span>
      </div>

      {cc.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded border border-steel-dark/60 bg-iron/40 p-2.5">
          <span className="font-display text-sm text-bone-mute/80">
            suggested from your Answer draft — tap to add (editable)
          </span>
          <div className="flex flex-wrap gap-1.5">
            {cc.map((c, i) => {
              const on = used.has(c.claim_name.trim().toLowerCase());
              const summary = c.theory ?? c.basis ?? c.grounds ?? "";
              return (
                <button key={i} type="button" disabled={on}
                  title={summary || undefined}
                  onClick={() => addFromCandidate(c.claim_name, summary)}
                  className={`rounded border px-2 py-0.5 font-display text-sm transition-colors ${
                    on
                      ? "border-steel-dark/60 text-bone-mute/40"
                      : "border-blue-bright/50 text-blue-bright hover:bg-blue-bright/10"
                  }`}>
                  {on ? "✓ " : "+ "}{c.claim_name}
                  {c.compulsory ? <span className="ml-1 text-ember-hot">· 13(a)</span> : null}
                </button>
              );
            })}
          </div>
          <span className="font-display text-sm text-bone-mute/50">
            · 13(a) = compulsory (same transaction) — waived if not raised now.
          </span>
        </div>
      )}

      {rows.length === 0 && cc.length === 0 && (
        <p className="font-display text-sm leading-snug text-bone-mute/70">
          Assert a cause of action against the plaintiff. Compulsory counterclaims (same transaction) must be raised in the answer — NCRCP 13(a). (Forge an Answer on the Berserkrs page to get suggested counterclaims here.)
        </p>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded border border-steel-dark/70 bg-iron/40 p-2.5">
          <div className="flex items-center gap-2">
            <input
              type="text" value={r.name} placeholder="Counterclaim (e.g. Breach of Contract)"
              onChange={(e) => set(i, { name: e.target.value })}
              className="min-w-0 flex-1 rounded border border-steel-dark bg-iron px-2.5 py-1.5 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-blue-bright/70 focus:outline-none"
            />
            <button type="button" onClick={() => remove(i)}
              className="shrink-0 font-display text-sm text-bone-mute/60 transition-colors hover:text-ember-hot">remove</button>
          </div>
          <input
            type="text" value={r.target} placeholder="Against (defaults to the plaintiff)"
            onChange={(e) => set(i, { target: e.target.value })}
            className="rounded border border-steel-dark bg-iron px-2.5 py-1.5 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-blue-bright/70 focus:outline-none"
          />
          <input
            type="text" value={r.summary} placeholder="One-line basis (optional)"
            onChange={(e) => set(i, { summary: e.target.value })}
            className="rounded border border-steel-dark bg-iron px-2.5 py-1.5 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-blue-bright/70 focus:outline-none"
          />
        </div>
      ))}
      <button type="button" onClick={add}
        className="self-start font-display text-sm text-blue-bright/80 transition-colors hover:text-blue-bright">
        + add a counterclaim
      </button>
    </div>
  );
}

function ActionRow({
  caseId,
  segmentId,
  action,
}: {
  caseId: string;
  segmentId: string;
  action: SegmentAction;
}) {
  const [open, setOpen] = useState(false);
  const [eventDate, setEventDate] = useState("");
  const [note, setNote] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const advance = useAdvanceSegment(caseId);
  const info = useContext(PlaybookContext).get(action.action_key);
  const targetsClaims = action.action_key === "motion_dismiss";
  const raisesCounterclaims = action.action_key === "answer";
  const { data: claimsData } = useClaimsSummary(targetsClaims ? caseId : undefined);
  const liveClaims = (claimsData?.claims ?? []).filter((c) => isLive(c.survival));
  const [picked, setPicked] = useState<string[]>([]);
  const [ccs, setCcs] = useState<CounterclaimDraft[]>([]);

  const cleanCcs = ccs
    .filter((c) => c.name.trim())
    .map((c) => ({
      name: c.name.trim(),
      target: c.target.trim() || null,
      summary: c.summary.trim() || null,
    }));
  const meta: Record<string, unknown> | undefined = targetsClaims
    ? { claim_ids: picked }
    : raisesCounterclaims && cleanCcs.length > 0
      ? { counterclaims: cleanCcs }
      : undefined;

  const needsDate = action.consequences.some((c) => c.requires_event_date);
  const accent = action.is_inaction || action.is_terminal ? "ember" : "blue";
  const dateLabel =
    action.actor === "court"
      ? "Date of the Court's order"
      : action.action_key === "stipulated_extension"
        ? "New agreed deadline"
        : "Effective date";

  return (
    <div className={`rounded border ${action.is_inaction ? "border-ember/40 bg-ember/10" : "border-steel-dark bg-iron/60"}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors ${accent === "ember" ? "hover:border-ember-bright/60" : "hover:bg-iron-2/70"}`}
      >
        <span className="flex w-full items-center justify-between">
          <span className="font-display text-base font-light tracking-tight text-bone">{action.label}</span>
          <span className="font-display text-sm text-bone-mute/70">{open ? "−" : "+"}</span>
        </span>
        {action.short_desc && (
          <span className="font-display text-sm leading-snug text-bone-dim">{action.short_desc}</span>
        )}
        <span className="mt-0.5 font-display text-sm text-bone-mute/70">
          {action.rule_cite}{action.brief_required ? " · brief required" : ""}
        </span>
      </button>

      {info && (
        <div className="border-t border-steel-dark/60 px-4 py-1.5">
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            className="font-display text-sm text-blue-bright/80 transition-colors hover:text-blue-bright"
          >
            ⓘ what this is {showInfo ? "▾" : "▸"}
          </button>
          {showInfo && <PlaybookPanel info={info} />}
        </div>
      )}

      {open && (
        <div className="border-t border-steel-dark/70 px-4 py-3">
          {action.consequences.length > 0 && (
            <div className="mb-3 flex flex-col gap-1.5">
              <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">consequence</span>
              {action.consequences.map((c) => (
                <p key={c.consequence_key} className="font-display text-sm leading-snug text-bone-dim">
                  → {c.label}
                  <span className="text-bone-mute"> · {ownerLabel(c.opens_owner)}{c.rule_cite ? ` · ${c.rule_cite}` : ""}</span>
                </p>
              ))}
            </div>
          )}

          {targetsClaims && (
            <div className="mb-3 flex flex-col gap-1.5">
              <span className="font-display text-sm text-bone-mute">which claims does this target?</span>
              <div className="flex flex-wrap gap-1.5">
                {liveClaims.map((c) => {
                  const on = picked.includes(c.id);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => setPicked((p) => (on ? p.filter((x) => x !== c.id) : [...p, c.id]))}
                      className={`rounded border px-2 py-0.5 font-display text-sm transition-colors ${
                        on ? "border-ember-bright/60 bg-ember/20 text-ember-hot" : "border-steel-dark text-bone-mute hover:text-bone-dim"
                      }`}>
                      {c.claim_name}
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={() => setPicked(liveClaims.map((c) => c.id))}
                className="self-start font-display text-sm text-blue-bright/80 hover:text-blue-bright">
                target all {liveClaims.length}
              </button>
            </div>
          )}

          {raisesCounterclaims && (
            <CounterclaimEditor caseId={caseId} rows={ccs} onChange={setCcs} />
          )}

          {needsDate && (
            <label className="mb-2 flex flex-col gap-1">
              <span className="font-display text-sm text-bone-mute">{dateLabel}</span>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full rounded border border-steel-dark bg-iron px-3 py-1.5 font-display text-sm text-bone focus:border-blue-bright/70 focus:outline-none"
              />
            </label>
          )}

          <label className="mb-3 flex flex-col gap-1">
            <span className="font-display text-sm text-bone-mute">note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. ECF no., grounds, who signed"
              className="w-full rounded border border-steel-dark bg-iron px-3 py-1.5 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-blue-bright/70 focus:outline-none"
            />
          </label>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={advance.isPending || (needsDate && !eventDate) || (targetsClaims && picked.length === 0)}
              onClick={() =>
                advance.mutate(
                  {
                    segmentId, actionKey: action.action_key,
                    eventDate: needsDate ? eventDate : null, note: note || null,
                    meta,
                  },
                  { onSuccess: () => { setOpen(false); setCcs([]); } },
                )
              }
              className={`px-4 py-1.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${accent === "ember" ? "border border-ember-bright/60 bg-ember/30 hover:bg-ember/50" : "border border-blue-bright/50 bg-iron-2/70 hover:bg-iron-2"}`}
            >
              {advance.isPending ? "recording…" : "record this move"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="font-display text-sm text-bone-mute/70 hover:text-bone-dim">cancel</button>
          </div>
          {advance.error && <p className="mt-2 font-display text-sm text-ember-hot">{advance.error.message}</p>}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- history -- */

function CaseHistory({ filings }: { filings: BoardFiling[] }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">case history</p>
      {filings.map((f) => (
        <div key={f.id} className="flex items-start justify-between gap-3 rounded border border-steel-dark/70 bg-iron/40 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-light text-bone">{f.title}</p>
            <p className="font-display text-sm text-bone-mute/70">{f.filed_by ?? "—"}{f.filed_at ? ` · ${fmtDate(f.filed_at)}` : ""}</p>
          </div>
          {f.is_operative && (
            <span className="shrink-0 rounded border border-blue-bright/40 px-2 py-0.5 font-display text-sm text-blue-bright">operative</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- trail -- */

function Trail({ segments, parties }: { segments: CaseSegment[]; parties: PartyRow[] }) {
  const done = segments
    .filter((s) => s.status === "resolved" || s.status === "waived")
    .sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  if (done.length === 0) return null;
  const nameOf = (id: string | null) =>
    id ? firstName(parties.find((p) => p.party_id === id)?.display_name ?? "") : "case-wide";
  return (
    <details className="group">
      <summary className="cursor-pointer list-none font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70 hover:text-bone-dim">
        the path so far ({done.length}) ▸
      </summary>
      <div className="mt-2 flex flex-col gap-1.5 border-l border-steel-dark pl-3">
        {done.map((s) => (
          <p key={s.id} className="font-display text-sm leading-snug text-bone-mute">
            <span className="text-bone-mute/60">[{nameOf(s.party_id)}]</span>{" "}
            <span className="text-bone-dim">{s.title}</span>
            {s.chosen_action_key ? <span className="text-bone-mute/70"> → {prettyAction(s.chosen_action_key)}</span> : null}
          </p>
        ))}
      </div>
    </details>
  );
}

/* ----------------------------------------------------- docket controls -- */

function DocketControls({ caseId, hasActions }: { caseId: string; hasActions: boolean }) {
  const undo = useUndoLastAction(caseId);
  const clear = useClearActionHistory(caseId);
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="mt-1 flex items-center justify-between gap-2 border-t border-steel-dark/60 pt-3">
      <button
        type="button"
        disabled={!hasActions || undo.isPending}
        onClick={() => undo.mutate()}
        className="font-display text-sm tracking-[0.08em] text-bone-mute/70 transition-colors hover:text-bone-dim disabled:cursor-not-allowed disabled:opacity-30"
      >
        ↶ {undo.isPending ? "undoing…" : "undo last action"}
      </button>
      {!confirming ? (
        <button type="button" onClick={() => setConfirming(true)} className="font-display text-sm tracking-[0.08em] text-bone-mute/70 transition-colors hover:text-ember-hot">
          clear history
        </button>
      ) : (
        <span className="flex items-center gap-2 font-display text-sm text-bone-mute">
          wipe the whole docket?
          <button type="button" disabled={clear.isPending} onClick={() => clear.mutate(undefined, { onSuccess: () => setConfirming(false) })} className="text-ember-hot hover:underline">
            {clear.isPending ? "clearing…" : "yes, clear"}
          </button>
          <button type="button" onClick={() => setConfirming(false)} className="text-bone-mute/60 hover:text-bone-dim">cancel</button>
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------- ruleset note -- */

function RulesetFooter({ ruleset }: { ruleset: Board["ruleset"] }) {
  if (!ruleset) return null;
  const years = ruleset.holiday_years ?? [];
  const span =
    years.length === 0 ? null : years.length === 1 ? `${years[0]}` : `${Math.min(...years)}–${Math.max(...years)}`;
  return (
    <div className="mt-1 border-t border-steel-dark/60 pt-3">
      <p className="font-display text-sm leading-snug text-bone-mute/70">
        rules loaded: {ruleset.rules_note ?? `${ruleset.name} (${ruleset.version})`}
        {span ? <> <span className="text-bone-mute/50">·</span> court holidays: NC {span}</> : null}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------- util -- */

function fmtDate(iso: string): string {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default ActionBoard;
